import type {
  Brand, Category, CustomFieldDefinition, Item, ItemInsights, ItemStockSummary, StockLevel,
} from '@erp/shared';
import { ITEM_COST_PERMISSIONS } from '@erp/shared';
import {
  ArrowLeft, ArrowUpDown, BarChart3, Barcode, Boxes, CalendarClock, EyeOff, FileText, History, ImageIcon, ImageOff,
  Info, Package, Pencil, Printer, ShoppingCart, Sparkles, Star, Tag, Tags, Truck, Undo2, Upload, Warehouse as WarehouseIcon, Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  Badge, Card, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/shadcn/index.js';
import { Button, CopyButton, EmptyState, RTE_CONTENT_CSS, StatCard, type StatTone } from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { useAnyPermission, usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { fetchAttachmentObjectUrl, isImageMime, listAttachments } from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney } from '../../lib/money.js';
import { getItemGroupPrices, type ItemGroupPrice } from '../../lib/pricing.js';
import { FileUploadDialog } from '../common/FileUploadDialog.js';
import { AdjustStockDialog } from '../inventory/AdjustStockDialog.js';

import { printItemBarcode } from './printBarcode.js';
import { ItemBranchesTab } from './profile/ItemBranchesTab.js';
import { ItemActivityTab, ItemBarcodesTab } from './profile/ItemCodesActivityTabs.js';
import { ItemReturnsTab, ItemTxnTab } from './profile/ItemDocumentsTabs.js';
import { ItemMovementsTab } from './profile/ItemMovementsTab.js';
import { categoryPath, ItemOverviewTab } from './profile/ItemOverviewTab.js';
import { ItemPricesTab } from './profile/ItemPricesTab.js';
import { ItemReportTab } from './profile/ItemReportTab.js';
import { biggestPack, fmtQty } from './profile/profileFormat.js';
import { moneyList, packLabel } from './profile/ProfileParts.js';

const TABS = ['overview', 'prices', 'stock', 'movements', 'sales', 'purchases', 'returns', 'report', 'barcodes', 'images', 'activity'] as const;
type TabId = typeof TABS[number];

/**
 * The product's profile — everything the shop knows about one product, on one
 * page: what it is and what it sells for (in every unit and every customer
 * group), how much of it there is and in which branch, every movement in and
 * out with the balance after it, who bought it and who sold it to us, what came
 * back, its codes, its pictures and who changed it.
 *
 * Each tab is gated the way its data is: stock by stock.read, anything that is
 * a COST (average cost, value, purchase prices) by ITEM_COST_PERMISSIONS, the
 * audit trail by audit_logs.read. A branch-confined user sees their branches'
 * figures only — the server narrows every read.
 */
export function ItemDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [search, setSearch] = useSearchParams();
  const { t, i18n } = useTranslation('items');
  const { t: tc } = useTranslation();
  const locale = i18n.language;
  const dir = useLayoutDirection();
  const canWrite = usePermission('items.write');
  const canFiles = usePermission('attachments.read');
  const canUpload = usePermission('attachments.write');
  const canStock = usePermission('stock.read');
  const canAdjust = usePermission('stock.adjust');
  const canAudit = usePermission('audit_logs.read');
  const canParties = usePermission('parties.read');
  const canCost = useAnyPermission(ITEM_COST_PERMISSIONS);
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const enabledModules = useEnabledModules(tenantId);
  const variantsEnabled = moduleEnabled(enabledModules, 'variants');
  const pricingEnabled = moduleEnabled(enabledModules, 'pricing');
  const multiCategory = moduleEnabled(enabledModules, 'multi_category');

  const [item, setItem] = useState<Item | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [defs, setDefs] = useState<CustomFieldDefinition[]>([]);
  const [groupPrices, setGroupPrices] = useState<ItemGroupPrice[]>([]);
  const [summary, setSummary] = useState<ItemStockSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [insights, setInsights] = useState<ItemInsights | null>(null);
  const [images, setImages] = useState<string[]>([]);
  const [imgVersion, setImgVersion] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustWarehouse, setAdjustWarehouse] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [moveWarehouse, setMoveWarehouse] = useState(search.get('warehouseId') ?? '');

  const isProduct = item ? item.itemType !== 'service' : true;
  const showStock = canStock && isProduct;
  const visible = (tab: TabId): boolean => {
    switch (tab) {
      case 'stock':
      case 'movements': return showStock;
      case 'purchases': return canCost;
      case 'activity': return canAudit;
      default: return true;
    }
  };
  const requested = search.get('tab') as TabId | null;
  const tab: TabId = requested && TABS.includes(requested) && visible(requested) ? requested : 'overview';
  const setTab = (next: string): void => {
    setSearch((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      p.delete('warehouseId');
      return p;
    }, { replace: true });
  };

  // The product and the catalogue around it.
  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    setItem(null); setNotFound(false);
    void (async () => {
      try {
        // Hidden units too, marked — the shop needs to see what it has taken
        // off sale, not wonder where the stand went.
        const r = await api().get<Item>(`/items/${id}?hiddenUnits=1`);
        if (active) setItem(r.data);
      } catch {
        if (active) setNotFound(true);
        return;
      }
      void api().get<{ items: Category[] }>('/categories').then((c) => active && setCategories(c.data.items)).catch(() => undefined);
      void api().get<{ items: Brand[] }>('/brands').then((b) => active && setBrands(b.data.items)).catch(() => undefined);
      void api().get<{ items: CustomFieldDefinition[] }>('/custom-fields').then((d) => active && setDefs(d.data.items.filter((x) => x.isActive))).catch(() => undefined);
      if (pricingEnabled) {
        void getItemGroupPrices(id).then((g) => active && setGroupPrices(g)).catch(() => undefined);
      }
    })();
    return () => { active = false; };
  }, [id, pricingEnabled]);

  // Figures that move with stock and sales — reloaded after an adjustment.
  const loadFigures = useCallback(async (): Promise<void> => {
    if (!id) return;
    const jobs: Array<Promise<unknown>> = [
      api().get<ItemInsights>(`/items/${id}/insights`).then((r) => setInsights(r.data)).catch(() => undefined),
    ];
    if (canStock) {
      jobs.push(api().get<ItemStockSummary>(`/stock/items/${id}/summary`, { params: { days: 30 } })
        .then((r) => { setSummary(r.data); setSummaryError(false); })
        .catch(() => setSummaryError(true)));
    }
    await Promise.all(jobs);
  }, [id, canStock]);
  useEffect(() => { void loadFigures(); }, [loadFigures]);

  // Images (auth-fetched object URLs); re-runs after an upload via imgVersion.
  useEffect(() => {
    if (!id || !canFiles) return undefined;
    let active = true;
    const made: string[] = [];
    void (async () => {
      try {
        const list = (await listAttachments('item', id))
          .filter((a) => isImageMime(a.mimeType))
          .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
        const urls: string[] = [];
        for (const a of list) {
          const u = await fetchAttachmentObjectUrl(a.id);
          made.push(u);
          urls.push(u);
        }
        if (active) setImages(urls);
        else made.forEach((u) => URL.revokeObjectURL(u));
      } catch {
        /* no images */
      }
    })();
    return () => { active = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [id, canFiles, imgVersion]);

  if (notFound) {
    return (
      <Card className="p-6">
        <p className="text-sm text-fg-muted">{tc('errors:notFound', { defaultValue: 'Not found.' })}</p>
        <Link to="/inventory/items" className="mt-3 inline-block text-sm text-brand underline">{t('title')}</Link>
      </Card>
    );
  }
  if (!item || !id) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
        <Card className="p-6"><Skeleton className="h-8 w-72" /><Skeleton className="mt-3 h-5 w-96" /></Card>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const brandName = item.brandId ? brands.find((b) => b.id === item.brandId)?.title ?? null : null;
  const path = categoryPath(categories, item.categoryId);
  const pack = biggestPack(item.units);
  const Icon = item.itemType === 'service' ? Wrench : Package;
  const base = item.unitOfMeasure;
  const nf = (v: number): string => formatNumber(v, { locale });
  const money = (v?: string | null, c?: string | null): string => (v && c ? formatMoney(v, { locale, currency: c }) : '—');

  const openAdjust = (warehouseId: string): void => { setAdjustWarehouse(warehouseId); setAdjustOpen(true); };
  const showMovements = (warehouseId: string): void => {
    setMoveWarehouse(warehouseId);
    setTab('movements');
  };
  const afterStockChange = async (): Promise<void> => {
    await loadFigures();
    setRefreshKey((k) => k + 1);
  };
  const printMain = (): void => printItemBarcode({
    sku: item.code,
    name: item.name,
    barcode: item.barcode ?? '',
    format: item.barcodeFormat,
    errorLabel: t('actions.printNoBarcode', { defaultValue: 'No barcode to print' }),
  });

  // ---- KPI strip ----
  const kpis: Array<{ key: string; label: string; value: string; hint?: string; icon: typeof Boxes; tone: StatTone; to?: string }> = [];
  if (showStock && summary) {
    const total = Number(summary.totals.quantity);
    const negatives = summary.totals.negativeWarehouses;
    kpis.push({
      key: 'onHand',
      label: t('profile.kpi.onHand', { defaultValue: 'On hand' }),
      value: `${fmtQty(total, locale)} ${base}`,
      hint: negatives > 0
        ? t('profile.stock.negativeBranches', { defaultValue: 'Branches below zero: {{n}}', n: negatives })
        : packLabel(t, total, pack, base, locale) ?? t('profile.kpi.inBranches', {
          defaultValue: 'In stock in {{n}} of {{of}} branches',
          n: summary.warehouses.filter((w) => Number(w.quantity) > 0).length,
          of: summary.warehouses.length,
        }),
      icon: Boxes,
      tone: total < 0 || negatives > 0 ? 'danger' : total === 0 ? 'warning' : 'success',
      to: '?tab=stock',
    });
    if (canCost) {
      kpis.push({
        key: 'value',
        label: t('profile.kpi.value', { defaultValue: 'Value at cost' }),
        value: moneyList(summary.values, locale),
        hint: t('profile.kpi.valueHint', { defaultValue: 'On hand × average cost, per currency' }),
        icon: WarehouseIcon,
        tone: 'info',
      });
    }
  }
  if (insights) {
    const s = insights.sold;
    kpis.push({
      key: 'sold',
      label: t('profile.kpi.sold', { defaultValue: 'Sold · {{days}} days', days: s.shortDays }),
      value: `${fmtQty(s.shortUnits, locale)} ${base}`,
      hint: t('profile.kpi.soldHint', {
        defaultValue: 'Invoices: {{n}} · {{days}} days: {{long}}',
        n: nf(s.shortInvoices), days: s.longDays, long: fmtQty(s.longUnits, locale),
      }),
      icon: ShoppingCart,
      tone: 'brand',
      to: '?tab=sales',
    });
    kpis.push({
      key: 'lastSale',
      label: t('profile.kpi.lastSale', { defaultValue: 'Last sale' }),
      value: insights.lastSale ? formatDate(insights.lastSale.docDate, { locale }) : '—',
      hint: insights.lastSale
        ? [insights.lastSale.docNumber, insights.lastSale.partyName].filter(Boolean).join(' · ')
        : t('profile.kpi.neverSold', { defaultValue: 'Not sold yet' }),
      icon: CalendarClock,
      tone: 'neutral',
      to: insights.lastSale ? `/sales/invoices/${insights.lastSale.invoiceId}` : undefined,
    });
    if (canCost) {
      const lp = insights.lastPurchase;
      kpis.push({
        key: 'lastPurchase',
        label: t('profile.kpi.lastPurchase', { defaultValue: 'Last purchase' }),
        value: lp ? money(lp.baseUnitCost, lp.currencyCode) : '—',
        hint: lp
          ? t('profile.kpi.lastPurchaseHint', {
            defaultValue: 'per {{unit}} · {{date}} · {{supplier}}',
            unit: base, date: formatDate(lp.docDate, { locale }), supplier: lp.partyName ?? lp.docNumber,
          })
          : t('profile.kpi.neverBought', { defaultValue: 'Never bought on a bill' }),
        icon: Truck,
        tone: 'neutral',
        to: lp ? `/purchasing/bills/${lp.billId}` : undefined,
      });
    }
    kpis.push({
      key: 'returns',
      label: t('profile.kpi.returns', { defaultValue: 'Returned' }),
      value: `${fmtQty(insights.returns.units, locale)} ${base}`,
      hint: t('profile.kpi.returnsHint', { defaultValue: 'Credit notes: {{n}}', n: nf(insights.returns.creditNotes) }),
      icon: Undo2,
      tone: Number(insights.returns.units) > 0 ? 'warning' : 'neutral',
      to: '?tab=returns',
    });
  }

  const tabIcon: Record<TabId, typeof Info> = {
    overview: Info, prices: Tags, stock: WarehouseIcon, movements: History, sales: ShoppingCart, purchases: Truck,
    returns: Undo2, report: BarChart3, barcodes: Barcode, images: ImageIcon, activity: FileText,
  };
  const tabLabel: Record<TabId, string> = {
    overview: t('detail.tabs.overview', { defaultValue: 'Overview' }),
    prices: t('profile.tabs.prices', { defaultValue: 'Prices' }),
    stock: t('profile.tabs.stock', { defaultValue: 'Stock' }),
    movements: t('detail.tabs.movements', { defaultValue: 'Stock movements' }),
    sales: t('detail.tabs.sales', { defaultValue: 'Sales' }),
    purchases: t('detail.tabs.purchase', { defaultValue: 'Purchases' }),
    returns: t('profile.tabs.returns', { defaultValue: 'Returns' }),
    report: t('detail.tabs.report', { defaultValue: 'Report' }),
    barcodes: t('profile.tabs.barcodes', { defaultValue: 'Barcodes' }),
    images: t('detail.tabs.images', { defaultValue: 'Images' }),
    activity: t('profile.tabs.activity', { defaultValue: 'Activity' }),
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
      <style>{RTE_CONTENT_CSS}</style>

      {/* ---------- Header ---------- */}
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            <Link
              to="/inventory/items"
              className="mt-1 inline-flex h-9 flex-none items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-3 text-sm font-semibold text-fg-muted hover:text-fg"
            >
              <ArrowLeft className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden />
              {tc('actions.back', { defaultValue: 'Back' })}
            </Link>
            <button
              type="button"
              onClick={() => setTab('images')}
              className="h-20 w-20 flex-none overflow-hidden rounded-xl border border-border bg-bg-subtle"
              aria-label={t('detail.tabs.images', { defaultValue: 'Images' })}
            >
              {images[0] ? (
                <img src={images[0]} alt="" className="h-full w-full object-contain" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-fg-subtle"><Icon className="h-7 w-7" aria-hidden /></span>
              )}
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold leading-tight">{item.name}</h1>
                <Badge variant={item.isActive ? 'success' : 'secondary'}>{t(`status.${item.isActive ? 'active' : 'inactive'}`)}</Badge>
                {item.isFeatured && <Badge variant="warning"><Star className="h-3 w-3" aria-hidden />{t('profile.badges.featured', { defaultValue: 'Featured' })}</Badge>}
                {item.isPublished && <Badge variant="info"><Sparkles className="h-3 w-3" aria-hidden />{t('profile.badges.published', { defaultValue: 'On the website' })}</Badge>}
                {item.isHidden && <Badge variant="secondary"><EyeOff className="h-3 w-3" aria-hidden />{t('profile.badges.hidden', { defaultValue: 'Hidden from the app' })}</Badge>}
                {item.isCombo && <Badge variant="info">{t('profile.flags.combo', { defaultValue: 'Combo' })}</Badge>}
                {item.itemType === 'service' && <Badge variant="secondary">{t('type.service')}</Badge>}
                {isProduct && !item.isInventoryTracked && <Badge variant="warning">{t('profile.badges.untracked', { defaultValue: 'Stock not tracked' })}</Badge>}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-fg-muted">
                <span className="inline-flex items-center gap-1">
                  <Tag className="h-3.5 w-3.5" aria-hidden />
                  <span dir="ltr" className="font-mono">{item.code}</span>
                  <CopyButton value={item.code} subject={t('detail.code', { defaultValue: 'Code / SKU' })} />
                </span>
                {item.barcode && (
                  <span className="inline-flex items-center gap-1">
                    <Barcode className="h-3.5 w-3.5" aria-hidden />
                    <span dir="ltr" className="font-mono">{item.barcode}</span>
                  </span>
                )}
                {path.length > 0 && <span className="truncate">{path.join(' › ')}</span>}
                {brandName && <span className="font-medium text-fg">{brandName}</span>}
              </div>
              <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="font-mono text-xl font-bold tabular-nums text-brand">{money(item.salePrice, item.saleCurrencyCode)}</span>
                <span className="text-xs text-fg-muted">{t('profile.header.perUnit', { defaultValue: 'per {{unit}}', unit: base })}</span>
                {item.units.filter((u) => u.isActive && u.salePrice).slice(0, 3).map((u) => (
                  <span key={u.id} className="text-sm text-fg-muted">
                    {u.name}: <span className="font-mono font-semibold tabular-nums text-fg">{money(u.salePrice, item.saleCurrencyCode)}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canAdjust && isProduct && (
              <Button variant="secondary" onClick={() => openAdjust('')}>
                <ArrowUpDown className="h-4 w-4" aria-hidden />{t('profile.actions.adjust', { defaultValue: 'Adjust stock' })}
              </Button>
            )}
            {item.barcode && (
              <Button variant="secondary" onClick={printMain}>
                <Printer className="h-4 w-4" aria-hidden />{t('profile.actions.printBarcode', { defaultValue: 'Print barcode' })}
              </Button>
            )}
            <Link to="/inventory/labels">
              <Button variant="ghost"><Tags className="h-4 w-4" aria-hidden />{t('labels.print', { defaultValue: 'Print labels' })}</Button>
            </Link>
            {canWrite && (
              <Link to={`/inventory/items/${item.id}/edit`}>
                <Button><Pencil className="h-4 w-4" aria-hidden />{tc('actions.edit')}</Button>
              </Link>
            )}
          </div>
        </div>
      </Card>

      {/* ---------- KPI strip ---------- */}
      {kpis.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {kpis.map((k) => (
            <StatCard key={k.key} label={k.label} value={<span className="text-xl">{k.value}</span>} hint={k.hint} icon={k.icon} tone={k.tone} to={k.to} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => <StatCard key={i} label="" value="" loading />)}
        </div>
      )}

      {/* dir: Radix Tabs default to ltr — the tab row follows the page language. */}
      <Tabs value={tab} onValueChange={setTab} dir={dir}>
        <TabsList className="h-auto flex-wrap justify-start">
          {TABS.filter(visible).map((k) => {
            const TabIcon = tabIcon[k];
            return (
              <TabsTrigger key={k} value={k} className="gap-1.5">
                <TabIcon className="h-4 w-4" aria-hidden />{tabLabel[k]}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <ItemOverviewTab
            item={item}
            categories={categories}
            brandName={brandName}
            defs={defs}
            groupPrices={groupPrices}
            variantsEnabled={variantsEnabled}
            multiCategory={multiCategory}
            summary={summary}
            pack={pack}
            canStock={canStock}
            onOpenStock={() => setTab('movements')}
            onOpenPrices={() => setTab('prices')}
          />
        </TabsContent>

        <TabsContent value="prices" className="mt-4">
          <ItemPricesTab item={item} groupPrices={groupPrices} pricingEnabled={pricingEnabled} canCost={canCost} canWrite={canWrite} />
        </TabsContent>

        {showStock && (
          <TabsContent value="stock" className="mt-4">
            <ItemBranchesTab
              summary={summary}
              error={summaryError}
              baseUnit={base}
              pack={pack}
              canCost={canCost}
              canAdjust={canAdjust}
              onAdjust={openAdjust}
              onShowMovements={showMovements}
            />
          </TabsContent>
        )}

        {showStock && (
          <TabsContent value="movements" className="mt-4">
            <ItemMovementsTab
              itemId={id}
              baseUnit={base}
              pack={pack}
              summary={summary}
              canCost={canCost}
              canAdjust={canAdjust}
              onChanged={() => void loadFigures()}
              refreshKey={refreshKey}
              warehouseId={moveWarehouse}
              onWarehouseChange={setMoveWarehouse}
            />
          </TabsContent>
        )}

        <TabsContent value="sales" className="mt-4">
          <ItemTxnTab itemId={id} kind="sales" baseUnit={base} />
        </TabsContent>

        {canCost && (
          <TabsContent value="purchases" className="mt-4">
            <ItemTxnTab itemId={id} kind="purchases" baseUnit={base} />
          </TabsContent>
        )}

        <TabsContent value="returns" className="mt-4">
          <ItemReturnsTab itemId={id} baseUnit={base} />
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          <ItemReportTab itemId={id} insights={insights} baseUnit={base} canParties={canParties} />
        </TabsContent>

        <TabsContent value="barcodes" className="mt-4">
          <ItemBarcodesTab item={item} />
        </TabsContent>

        <TabsContent value="images" className="mt-4">
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-wide text-fg-muted">{t('detail.tabs.images', { defaultValue: 'Images' })}</h2>
              {canUpload && (
                <Button variant="secondary" size="sm" onClick={() => setUploadOpen(true)}>
                  <Upload className="h-4 w-4" aria-hidden />{t('detail.uploadImages', { defaultValue: 'Upload' })}
                </Button>
              )}
            </div>
            {!canFiles ? (
              <p className="text-sm text-fg-subtle">{t('detail.noImageAccess', { defaultValue: 'You don’t have access to files.' })}</p>
            ) : images.length === 0 ? (
              <EmptyState icon={<ImageOff className="h-7 w-7" aria-hidden />} title={t('detail.noImages', { defaultValue: 'No images yet' })} />
            ) : (
              <div className="flex flex-wrap gap-3">
                {images.map((u, i) => (
                  <div key={u} className="relative h-40 w-40 overflow-hidden rounded-xl border border-border bg-bg-subtle">
                    <img src={u} alt="" className="h-full w-full object-contain" />
                    {i === 0 && (
                      <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-white">
                        {t('create.primaryImage', { defaultValue: 'Main' })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
          {canUpload && (
            <FileUploadDialog
              entityType="item"
              entityId={id}
              open={uploadOpen}
              onOpenChange={setUploadOpen}
              title={t('files.title', { defaultValue: 'Files — {{name}}', name: item.name })}
              onChange={() => setImgVersion((v) => v + 1)}
            />
          )}
        </TabsContent>

        {canAudit && (
          <TabsContent value="activity" className="mt-4">
            <ItemActivityTab itemId={id} />
          </TabsContent>
        )}
      </Tabs>

      {canAdjust && isProduct && (
        <AdjustStockDialog
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          levels={(summary?.warehouses ?? []).map((w): StockLevel => ({
            itemId: id,
            itemCode: item.code,
            itemName: item.name,
            warehouseId: w.warehouseId,
            warehouseCode: w.code,
            warehouseName: w.name,
            quantity: w.quantity,
            avgCost: w.avgCost ?? '0.0000',
            avgCostCurrencyCode: w.avgCostCurrencyCode,
            updatedAt: w.lastMovementAt ?? item.updatedAt,
          }))}
          initial={{ itemId: id, warehouseId: adjustWarehouse }}
          lockedItem={{ id, code: item.code, name: item.name }}
          onDone={afterStockChange}
        />
      )}
    </div>
  );
}
